import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Portfolio() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const companies = [
    {
      name: 'The Orange Bamboo',
      url: 'https://www.theorangebamboo.com/',
      sectorKey: 'tob_sector',
      descKey: 'tob_desc',
      tagKey: 'tob_tag',
      image: 'https://public.readdy.ai/ai/img_res/5040ff7b9a6f80f89e59ca586457e928.jpg',
      color: '#F59E0B',
    },
    {
      name: 'Citadel B',
      url: 'https://citadelb.com/',
      sectorKey: 'cb_sector',
      descKey: 'cb_desc',
      tagKey: 'cb_tag',
      image: 'https://readdy.ai/api/search-image?query=bitcoin%20custody%20security%20vault%20digital%20assets%20crypto%20minimalist%20dark%20red%20geometric%20abstract%20professional%20corporate%20clean%20background&width=600&height=360&seq=citb1&orientation=landscape',
      color: '#EF4444',
    },
  ];

  return (
    <section id="portfolio" className="bg-white py-28 md:py-36">
      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          className={`mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-4 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <div>
            <span className="font-['JetBrains_Mono'] text-[10px] tracking-[0.22em] uppercase text-[#D4A853] block mb-4">
              {t('portfolio_label')}
            </span>
            <h2 className="font-['Instrument_Serif'] text-4xl md:text-5xl text-[#0B0F1A] leading-tight">
              {t('portfolio_title')}
            </h2>
          </div>
          <p className="font-['DM_Sans'] text-[#6B7280] text-sm md:text-base max-w-xs md:text-right leading-relaxed">
            {t('portfolio_sub')}
          </p>
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-[#E8E4DC] mb-14" />

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {companies.map((company, i) => (
            <a
              key={company.name}
              href={company.url}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={`group block border border-[#E8E4DC] rounded-2xl overflow-hidden hover:border-[#D4A853]/50 transition-all duration-500 cursor-pointer ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              {/* Image */}
              <div className="w-full h-52 overflow-hidden relative">
                <img
                  src={company.image}
                  alt={company.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <span
                  className="absolute top-4 left-4 font-['JetBrains_Mono'] text-[10px] tracking-[0.18em] uppercase px-3 py-1 rounded-full"
                  style={{ backgroundColor: `${company.color}22`, color: company.color, border: `1px solid ${company.color}44` }}
                >
                  {t(company.tagKey)}
                </span>
              </div>

              {/* Body */}
              <div className="p-7">
                <p className="font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] uppercase text-[#6B7280] mb-3">
                  {t(company.sectorKey)}
                </p>
                <h3 className="font-['Instrument_Serif'] text-2xl text-[#0B0F1A] mb-3 group-hover:text-[#D4A853] transition-colors duration-300">
                  {company.name}
                </h3>
                <p className="font-['DM_Sans'] text-[#6B7280] text-sm leading-relaxed mb-6">
                  {t(company.descKey)}
                </p>
                <div className="flex items-center gap-2 text-[#D4A853] font-['JetBrains_Mono'] text-xs tracking-widest uppercase">
                  <span>{t('portfolio_cta')}</span>
                  <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
                    <i className="ri-arrow-right-line text-sm" />
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Footer note */}
        <div
          className={`mt-14 flex items-center gap-3 justify-center transition-all duration-700 delay-500 ${visible ? 'opacity-100' : 'opacity-0'}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#D4A853] inline-block" />
          <p className="font-['JetBrains_Mono'] text-xs text-[#6B7280] tracking-wide">
            {t('portfolio_note')}
          </p>
        </div>
      </div>
    </section>
  );
}
